import { get, isPlainObject } from 'es-toolkit/compat';
import { FieldNamesMarkedBoolean } from 'react-hook-form';

import { FieldValues, NestedKeyOf } from '@common-lib';

/**
 * Utils for converting between form input state and model data.
 *
 * Difference between both is that for example in a form an
 * undefined value is an empty string whereas when converting
 * back to the model the empty string should be undefined again.
 */
export const formUtils = {
  string: {
    toModel(input: string | undefined): string | undefined {
      if (input && input.length > 0) {
        return input.trim();
      }

      return undefined;
    },
    toInput(model: string | undefined): string {
      return model === undefined ? '' : model;
    },
  },
  date: {
    toModel(input: Date | string | undefined): string | undefined {
      if (typeof input === 'string') {
        const value = formUtils.string.toModel(input);

        if (value === undefined) {
          return undefined;
        }

        return new Date(value).toISOString();
      }

      return input?.toISOString();
    },
    toInput(model: Date | string | undefined): string {
      if (model === undefined) {
        return '';
      } else if (typeof model === 'string') {
        if (model.length === 0) {
          return '';
        }

        return model;
      }

      return model.toISOString();
    },
  },
  number: {
    toModel(input: number | string | undefined): number | undefined {
      if (typeof input === 'number') {
        return input;
      }

      const value = formUtils.string.toModel(input);
      return value === undefined ? undefined : Number(value);
    },
    toInput(model: number | string | undefined): string {
      return model?.toString() ?? '';
    },
  },
  currency: {
    toModel(input: number | string | undefined): number | undefined {
      const value = formUtils.number.toModel(input);

      if (value === undefined) {
        return undefined;
      }

      return Math.round((value + Number.EPSILON) * 100);
    },
    toInput(model: number | undefined): string {
      if (model === undefined) {
        return '';
      }

      return (Math.round(model + Number.EPSILON) / 100).toFixed(2);
    },
  },
  percentage: {
    toModel(input: number | string | undefined): number | undefined {
      const value = formUtils.number.toModel(input);

      if (value === undefined) {
        return undefined;
      }

      return Math.round((value + Number.EPSILON) * 100);
    },
    toInput(model: number | undefined): string {
      if (model === undefined) {
        return '';
      }

      return (Math.round(model + Number.EPSILON) / 100).toFixed(2);
    },
  },
  vat: {
    toInput(model: number | undefined): string {
      if (model === undefined) {
        return '';
      }

      return (Math.round(model + Number.EPSILON) / 1000000).toFixed(2);
    },
  },
  vatRate: {
    toInput(model: number | undefined): number {
      if (model === undefined) {
        return 0;
      }

      return Math.round(model + Number.EPSILON) / 10000;
    },
  },
  array: {
    /**
     * Maps an array of items. If the array is undefined or empty undefined
     * will be returned
     */
    toOptional<TA, TB>(
      input: TA[] | undefined,
      itemMapper: (input: TA) => TB,
    ): TB[] | undefined {
      if (input === undefined || input.length === 0) {
        return undefined;
      }

      return input.map(itemMapper);
    },

    /**
     * Maps an array of items. If the array is undefined, undefined will be returned.
     */
    to<TA, TB>(
      input: TA[] | undefined,
      itemMapper: (input: TA) => TB,
    ): TB[] | undefined {
      if (input === undefined) {
        return undefined;
      }

      return input.map(itemMapper);
    },
  },

  /**
   * Checks if any of the fields in the given list are dirty in a
   * react-hook-form dirtyFields object. Supports checking parent
   * properties as well as nested properties. When checking a
   * parent property all nested properties will be checked to find
   * if any of them are dirty.
   */
  isAnyFieldDirty<TFormInput extends FieldValues>(
    dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<TFormInput>>>,
    fieldsToCheck: NestedKeyOf<TFormInput>[],
  ): boolean {
    return fieldsToCheck.some((field) => {
      const value: any = get(dirtyFields, field, false);

      if (typeof value === 'boolean') {
        return value;
      } else if (Array.isArray(value)) {
        return value.some((subValue) => {
          if (Array.isArray(subValue)) {
            // Array with nested arrays
            return this.isAnyFieldDirty(subValue, Object.keys(subValue));
          } else if (isPlainObject(subValue)) {
            // Array with objects
            return this.isAnyFieldDirty(subValue, Object.keys(subValue));
          } else if (typeof subValue === 'boolean') {
            return subValue;
          } else {
            throw Error(
              `Unexpected value type when checking if field is dirty: ${typeof subValue}`,
            );
          }
        });
      } else {
        return this.isAnyFieldDirty(value, Object.keys(value));
      }
    });
  },
};
